import {
  Button,
  CircularProgress,
  List,
  ListItemButton,
  Stack,
  Typography,
} from '@mui/material'
import { Dialog, DialogContent, DialogFooter } from '../dialog'
import { OverlayState } from '@/hooks/useOverlay'
import { ProjectCategory } from '@/types/projects'
import {
  useCreateProjectFromTemplate,
  useGetProjectTemplates,
} from '@/service-library/hooks/project-templates'
import { useRootOrganization } from '../organizations/RootOrganizationProvider'
import { ProjectTemplate } from '@/types/project-templates'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { showInfoSnackbar } from '@/utils/snackbars'
import { blue, orange } from '@mui/material/colors'

function GradientCircularProgress() {
  return (
    <>
      <svg width={0} height={0}>
        <defs>
          <linearGradient id="my_gradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={blue['A700']} />
            <stop offset="100%" stopColor={orange['A700']} />
          </linearGradient>
        </defs>
      </svg>
      <CircularProgress
        size={64}
        sx={{ 'svg circle': { stroke: 'url(#my_gradient)' } }}
      />
    </>
  )
}

function TemplateList({
  projectTemplates,
  handleCreateProject,
  title,
}: {
  projectTemplates: ProjectTemplate[]
  handleCreateProject: (template: ProjectTemplate) => void
  title: string
}) {
  if (projectTemplates.length === 0) return null
  return (
    <Stack spacing={0.5}>
      <Typography variant="caption" color="text.secondary" sx={{ px: 3 }}>
        {title}
      </Typography>
      <List dense disablePadding>
        {projectTemplates.map((template) => {
          return (
            <ListItemButton
              key={template.id}
              onClick={() => handleCreateProject(template)}
              sx={{ px: 3 }}
            >
              {template.project_display_name}
            </ListItemButton>
          )
        })}
      </List>
    </Stack>
  )
}

type ProjectTemplateDialogProps = {
  overlay: OverlayState
  category: ProjectCategory
}

export default function ProjectTemplateDialog({
  overlay,
  category,
}: ProjectTemplateDialogProps) {
  const { rootOrganization } = useRootOrganization()

  // Global templates
  const { projectTemplates: globalTemplates } = useGetProjectTemplates({
    filters: {
      limit: '1000',
    },
  })

  // Account specific templates
  const { projectTemplates: accountTemplates } = useGetProjectTemplates({
    filters: {
      limit: '1000',
      account_id: rootOrganization.account_id,
    },
  })

  const [creating, setCreating] = useState(false)
  const { createProjectFromTemplate } = useCreateProjectFromTemplate()
  const navigate = useNavigate()

  function handleCreateProject(template: ProjectTemplate) {
    showInfoSnackbar('Creating project...', {
      autoHideDuration: 5000,
    })
    setCreating(true)
    createProjectFromTemplate({
      template_id: template.id,
      category_id: category.id,
      org_id: rootOrganization.id,
    }).then(({ id }) => {
      // TODO: It would be good to do this dynamically instead of hoping five seconds is enough...
      setTimeout(() => {
        // We delay navigation to allow the project creation to complete.
        overlay.close()
        navigate(`/projects/${id}`)
      }, 5000)
    })
  }

  return (
    <Dialog
      {...overlay}
      title={creating ? undefined : 'Select a Project Template'}
      maxWidth="xs"
    >
      {creating ? (
        <Stack
          spacing={4}
          sx={{ height: 300 }}
          alignItems="center"
          justifyContent="center"
        >
          <GradientCircularProgress />

          <Typography variant="h6" color="text.secondary">
            Creating project...
          </Typography>
        </Stack>
      ) : (
        <>
          <DialogContent disablePadding>
            <Stack spacing={2}>
              {globalTemplates.length === 0 &&
                accountTemplates.length === 0 && (
                  <Typography variant="body1" sx={{ px: 3 }}>
                    No templates available.
                  </Typography>
                )}

              <TemplateList
                projectTemplates={globalTemplates}
                handleCreateProject={handleCreateProject}
                title="Global Templates"
              />

              <TemplateList
                projectTemplates={accountTemplates}
                handleCreateProject={handleCreateProject}
                title="Your Templates"
              />
            </Stack>
          </DialogContent>
          <DialogFooter>
            <Button variant="text" onClick={overlay.close}>
              Cancel
            </Button>
          </DialogFooter>
        </>
      )}
    </Dialog>
  )
}
